import {
  ConditionComparator,
  FieldOption,
  FormFieldCondition,
  FormFieldConditionForm,
  FormFieldConditionJourney,
  FormFieldValue,
  filterExcludedOptions,
  isFormSourceCondition,
  withCustomConditionOption,
} from '@components/form/v2';
import type { SelectOption } from '@components/select';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
} from '@components/text-editors/addons/field-mapping';
import {
  findVariableField,
  isVariableSourceMissing,
} from '@components/text-editors/addons/field-mapping-variable-label';

// Type-only: a runtime import of ./context would close a module cycle through
// create-builder-store back into this file.
import type { MarketplaceIntegrationWithLists, PredefinedOptionsLists } from './context';
import type { StateField, StateFieldMap } from './state';

export type ConditionDraftForm = {
  source: 'form';
  id: string;
  questionId?: string;
  comparator?: ConditionComparator;
  value?: FormFieldValue;
};

export const MULTI_COMPARATORS: ReadonlySet<ConditionComparator> = new Set(['any_of', 'none_of']);

export const isMultiComparator = (comparator: ConditionComparator): boolean =>
  MULTI_COMPARATORS.has(comparator);

const VALUELESS_COMPARATORS = new Set<string>(['empty', 'nempty']);

const comparatorRequiresValue = (comparator: ConditionComparator): boolean =>
  !VALUELESS_COMPARATORS.has(comparator);

export const hasValue = (value: FormFieldValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

export const canSaveCondition = (condition: ConditionDraftForm): boolean => {
  if (!condition.questionId) return false;
  if (!condition.comparator) return false;
  if (comparatorRequiresValue(condition.comparator) && !hasValue(condition.value)) {
    return false;
  }
  return true;
};

const reshapeValueForComparator = (
  value: FormFieldValue | undefined,
  willBeMulti: boolean,
): FormFieldValue => {
  if (willBeMulti) {
    if (Array.isArray(value)) return value;
    return hasValue(value) ? [value as string | number] : [];
  }
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
};

export const applyComparatorChange = <T extends FormFieldCondition>(
  condition: T,
  next: ConditionComparator,
): T => {
  if (next === condition.comparator) return condition;
  const wasMulti = isMultiComparator(condition.comparator);
  const willBeMulti = isMultiComparator(next);
  if (wasMulti === willBeMulti) {
    return { ...condition, comparator: next };
  }
  return {
    ...condition,
    comparator: next,
    value: reshapeValueForComparator(condition.value, willBeMulti),
  };
};

const toggleMultiValue = (
  current: FormFieldValue | undefined,
  candidate: string,
): Array<string | number> => {
  const arr = Array.isArray(current) ? current : [];
  return arr.includes(candidate) ? arr.filter((entry) => entry !== candidate) : [...arr, candidate];
};

export const applyValueChange = <T extends FormFieldCondition>(
  condition: T,
  next: FormFieldValue,
): T => {
  if (!isMultiComparator(condition.comparator) || typeof next !== 'string') {
    return { ...condition, value: next };
  }
  return { ...condition, value: toggleMultiValue(condition.value, next) };
};

export const isCompleteCondition = (condition: FormFieldCondition): boolean => {
  if (condition.source === 'form' && !condition.questionId) return false;
  if (condition.source === 'journey' && !condition.variable) return false;
  if (!condition.comparator) return false;
  const valueIsArray = Array.isArray(condition.value);
  const expectArray = isMultiComparator(condition.comparator);
  if (expectArray !== valueIsArray) return false;
  if (comparatorRequiresValue(condition.comparator) && !hasValue(condition.value)) {
    return false;
  }
  return true;
};

export const getRenderableConditionCount = (conditions: FormFieldCondition[]): number =>
  conditions.length;

const optionValueOf = (entry: FormFieldValue | null | undefined): string | undefined => {
  if (entry === null || entry === undefined || entry === '') return undefined;
  return String(entry);
};

// Rows stored before the API inlined option values carry the shortId; the backend
// accepts either key.
const knownValuesCache = new WeakMap<object, ReadonlySet<string>>();

const knownOptionValues = (
  options: ReadonlyArray<{ value: string; shortId?: string }>,
): ReadonlySet<string> => {
  const cached = knownValuesCache.get(options);
  if (cached) return cached;
  const values = new Set<string>();
  for (const option of options) {
    values.add(option.value);
    if (option.shortId) values.add(option.shortId);
  }
  knownValuesCache.set(options, values);
  return values;
};

/**
 * Values a condition references that are no longer present in the field's current
 * option set. A non-empty result means the condition can never match: the option it
 * was built against was replaced (e.g. the field was switched from manually-entered
 * options to a predefined list, which swaps every option's underlying value).
 */
export const getMissingOptionValues = (
  value: FormFieldValue | null | undefined,
  options: ReadonlyArray<{ value: string; shortId?: string }> | null,
): string[] => {
  if (options === null) return [];
  const knownValues = knownOptionValues(options);
  const entries = Array.isArray(value) ? value : [value];
  const missing = entries
    .map(optionValueOf)
    .filter((entry): entry is string => entry !== undefined && !knownValues.has(entry));
  return [...new Set(missing)];
};

export type ResolvedFieldOptions = {
  options: FieldOption[];
  /** False when the referenced list is in none of the loaded sources — not yet
   * loaded or deleted. Callers must not treat the stored value as broken then. */
  isResolved: boolean;
};

/**
 * Filtered list options, shared across every caller that resolves the same list
 * out of the same loaded set.
 *
 * The stale-condition indicators subscribe two to three times per element and
 * recompute on every store snapshot; without this each keystroke re-filtered
 * every referenced list once per subscriber. Keyed on the loaded array's
 * identity, which react-query replaces when the lists change, so nothing is
 * invalidated by hand.
 */
const filteredListOptionsCache = new WeakMap<object, Map<string, FieldOption[]>>();

const filteredListOptions = (
  owner: object,
  key: string,
  compute: () => FieldOption[],
): FieldOption[] => {
  let cache = filteredListOptionsCache.get(owner);
  if (!cache) {
    cache = new Map();
    filteredListOptionsCache.set(owner, cache);
  }
  const cached = cache.get(key);
  if (cached) return cached;
  const computed = compute();
  cache.set(key, computed);
  return computed;
};

/**
 * The single definition of a field's effective option set. Hidden lists back
 * element-local options, so while editing the field's own array is the live
 * truth and the list is only the server's last-saved snapshot.
 */
export const resolveFieldOptions = (
  field: {
    options: FieldOption[];
    optionsFromListId?: string | null;
    excludedOptions?: string[] | null;
  },
  predefinedOptionsLists: PredefinedOptionsLists,
  marketplaceIntegrationLists: MarketplaceIntegrationWithLists[] = [],
): ResolvedFieldOptions => {
  if (!field.optionsFromListId) {
    return { options: field.options, isResolved: true };
  }

  const excludedOptions = field.excludedOptions ?? [];
  const cacheKey = JSON.stringify([field.optionsFromListId, excludedOptions]);
  const withoutExcluded = (options: FieldOption[]): FieldOption[] => {
    return options.filter((option) => !excludedOptions.includes(option.value));
  };

  const predefinedList = predefinedOptionsLists.find(
    (list) => list.value === field.optionsFromListId,
  );
  if (predefinedList) {
    if (predefinedList.isHidden) {
      return { options: field.options, isResolved: true };
    }
    return {
      options: filteredListOptions(predefinedOptionsLists, cacheKey, () =>
        withoutExcluded(predefinedList.options),
      ),
      isResolved: true,
    };
  }

  for (const integration of marketplaceIntegrationLists) {
    const marketplaceList = integration.lists.find(
      (list) => list.shortId === field.optionsFromListId,
    );
    if (marketplaceList) {
      return {
        options: filteredListOptions(marketplaceIntegrationLists, cacheKey, () =>
          withoutExcluded(marketplaceList.options),
        ),
        isResolved: true,
      };
    }
  }

  return { options: field.options, isResolved: false };
};

export const resolveQuestionOptions = (
  question: StateField,
  predefinedOptionsLists: PredefinedOptionsLists,
  marketplaceIntegrationLists: MarketplaceIntegrationWithLists[],
): ReadonlyArray<{ value: string; label: string }> | null => {
  if (!('options' in question)) return null;
  const { options, isResolved } = resolveFieldOptions(
    question,
    predefinedOptionsLists,
    marketplaceIntegrationLists,
  );
  // The list may not have loaded yet — that is not the same as "broken".
  return isResolved ? withCustomConditionOption(question, options) : null;
};

export const journeyVariableGroupsAreNonEmpty = (
  groups: ReadonlyArray<FieldMappingGroup>,
): boolean => groups.some((group) => group.fields.length > 0);

export const findCanonicalVariable = (
  groups: FieldMappingGroup[],
  variable: FieldMappingFieldItem,
): FieldMappingFieldItem | undefined => {
  return findVariableField(
    variable,
    groups.flatMap((group) => group.fields),
  );
};

const SELECT_LIKE_VARIABLE_TYPES: ReadonlySet<FieldType> = new Set([
  'select',
  'multi_select',
  'options',
]);

const isSelectLikeVariable = (variable: FieldMappingFieldItem): boolean =>
  Boolean(variable.type && SELECT_LIKE_VARIABLE_TYPES.has(variable.type));

// The only way to tell a deleted upstream question (action present, field gone)
// from variable groups that have not loaded.
const upstreamActionIsLive = (
  groups: ReadonlyArray<FieldMappingGroup>,
  variable: FieldMappingFieldItem,
): boolean => {
  if (variable.source !== 'action' || !variable.action) return false;
  return groups.some((group) => group.fields.some((field) => field.action === variable.action));
};

/**
 * Stored values of a journey-source condition that no longer exist among the
 * source variable's current options. The stored variable's own options are a
 * pick-time snapshot, so only the canonical variable can settle staleness.
 */
export const getStaleJourneyConditionValues = (
  condition: FormFieldConditionJourney,
  canonicalVariable: FieldMappingFieldItem | undefined,
  currentOptions: ReadonlyArray<{ value: string }>,
): string[] => {
  if (!canonicalVariable?.type || !SELECT_LIKE_VARIABLE_TYPES.has(canonicalVariable.type)) {
    return [];
  }
  if (currentOptions.length === 0) return [];
  return getMissingOptionValues(condition.value, currentOptions);
};

export const normalizeVariableOptions = (variable: FieldMappingFieldItem): SelectOption[] => {
  const options = variable.options;
  if (!options) return [];
  const mapped = options
    .map((option) => {
      if ('label' in option) return { value: option.value, label: option.label };
      return { value: option.value, label: option.display };
    })
    .filter((option): option is SelectOption => Boolean(option.value));
  return withCustomConditionOption(variable, mapped);
};

// Company lists only: the condition row resolves the same id through the
// company-lists endpoint, and the two surfaces must agree. `isResolved` separates
// "offers nothing to match against", which blocks Publish, from "list not loaded",
// which proves nothing.
const resolveJourneyVariableOptions = (
  variable: FieldMappingFieldItem,
  sources: ConditionOptionSources,
): { options: ReadonlyArray<{ value: string }>; isResolved: boolean } => {
  const inline = normalizeVariableOptions(variable);
  if (inline.length > 0) return { options: inline, isResolved: true };
  if (!variable.optionsFromListId) return { options: [], isResolved: true };

  const predefinedList = sources.predefinedOptionsLists.find(
    (list) => list.value === variable.optionsFromListId,
  );
  if (!predefinedList) return { options: [], isResolved: false };
  return {
    options: withCustomConditionOption(
      variable,
      filterExcludedOptions(predefinedList.options, variable.excludedOptions),
    ),
    isResolved: true,
  };
};

export type ConditionOptionSources = {
  questionsById: StateFieldMap;
  predefinedOptionsLists: PredefinedOptionsLists;
  marketplaceIntegrationLists?: MarketplaceIntegrationWithLists[];
  variableGroups?: FieldMappingGroup[];
};

const conditionReferencesMissingOption = (
  condition: FormFieldCondition,
  sources: ConditionOptionSources,
): boolean => {
  if (isFormSourceCondition(condition)) {
    const question = sources.questionsById[condition.questionId];
    if (!question || !('options' in question)) return false;
    // Marketplace lists are passed empty: the backend enriches from company lists
    // only, so a marketplace-backed question never blocks Publish.
    const options = resolveQuestionOptions(question, sources.predefinedOptionsLists, []);
    // The backend skips a question whose option set is empty.
    if (!options?.length) return false;
    return getMissingOptionValues(condition.value, options).length > 0;
  }

  const variable = condition.variable;
  if (!variable) return false;
  // A severed source carries its own broken-variable chip on the row, and the
  // backend skips an action it can no longer look up.
  if (isVariableSourceMissing(variable)) return false;

  const groups = sources.variableGroups ?? [];
  const canonicalVariable = findCanonicalVariable(groups, variable);
  if (!canonicalVariable) {
    // The action is live but the question is gone. The backend blocks Publish on this
    // shape, so it must badge with no canonical option set to compare against.
    return isSelectLikeVariable(variable) && upstreamActionIsLive(groups, variable);
  }
  if (!isSelectLikeVariable(canonicalVariable)) return false;

  const { options, isResolved } = resolveJourneyVariableOptions(canonicalVariable, sources);
  if (!isResolved) return false;
  // Unlike a local question, an upstream field with no options blocks Publish.
  if (options.length === 0) return true;
  return getMissingOptionValues(condition.value, options).length > 0;
};

/**
 * Whether any of an element's conditions compares against an option that no
 * longer exists on its source. Drives the canvas, element-tree and
 * Conditions-tab warning indicators so a broken element can be spotted without
 * opening it.
 *
 * Mirrors what `has_stale_form_config_option_reference` blocks Publish on, so a
 * blocked publish always has something to click. A list in none of the loaded
 * sources reports "not broken": a badge that flashes on first paint and then
 * vanishes is worse than no badge.
 */
export const hasStaleConditionOptionReference = (
  conditions: ReadonlyArray<FormFieldCondition>,
  sources: ConditionOptionSources,
): boolean => conditions.some((condition) => conditionReferencesMissingOption(condition, sources));

const TEXT_LIKE_COMPARATORS: ReadonlyArray<ConditionComparator> = [
  'is',
  'not_is',
  'starts',
  'not_starts',
  'contains',
  'not_contains',
];

const FORM_COMPARATORS_BY_FIELD_TYPE: Partial<
  Record<FieldType, ReadonlyArray<ConditionComparator>>
> = {
  select: ['is', 'not_is', 'any_of', 'none_of'],
  multi_select: ['any_of', 'none_of'],
  options: ['is', 'not_is', 'any_of', 'none_of'],
  date: ['before', 'on', 'after'],
  text: TEXT_LIKE_COMPARATORS,
  email: TEXT_LIKE_COMPARATORS,
  phone: TEXT_LIKE_COMPARATORS,
  paragraph: TEXT_LIKE_COMPARATORS,
  number: ['is', 'not_is'],
  decimal: ['is', 'not_is'],
  boolean: ['is', 'not_is'],
  kennitala: TEXT_LIKE_COMPARATORS,
};

export const getComparatorsForFieldType = (
  fieldType: FieldType | undefined,
): ReadonlyArray<ConditionComparator> => {
  if (!fieldType) return [];
  return FORM_COMPARATORS_BY_FIELD_TYPE[fieldType] ?? [];
};

export const buildResetFormCondition = (
  conditionId: string,
  newQuestionId: string,
  availableComparators: ReadonlyArray<ConditionComparator>,
): FormFieldConditionForm => {
  const comparator = availableComparators[0] ?? 'is';
  return {
    source: 'form',
    id: conditionId,
    questionId: newQuestionId,
    comparator,
    value: isMultiComparator(comparator) ? [] : '',
  };
};

export const buildResetJourneyCondition = (
  conditionId: string,
  variable: FieldMappingFieldItem,
  availableComparators: ReadonlyArray<ConditionComparator>,
): FormFieldConditionJourney => {
  const comparator = availableComparators[0] ?? 'is';
  return {
    source: 'journey',
    id: conditionId,
    variable,
    comparator,
    value: isMultiComparator(comparator) ? [] : '',
  };
};
